package com.example.hukakmp.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.ModalDrawerSheet
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.hukakmp.model.HukaUser

private val DrawerBackground = Color(0xFF031B29)
private val DrawerCard = Color(0xFF082E49)
private val DrawerEdge = Color(0xFF0877BE)
private val DrawerBlue = Color(0xFF22C7F3)
private val DrawerCyan = Color(0xFF12C9D7)
private val DrawerText = Color(0xFFF7FBFF)
private val DrawerSubText = Color(0xFFC6DCEC)

private data class DrawerEntry(
    val key: String,
    val title: String,
    val subtitle: String,
    val accent: Color,
    val available: Boolean,
)

@Composable
fun HukaDrawerContent(
    user: HukaUser,
    selected: String,
    onInicio: () -> Unit,
    onNuevoParte: () -> Unit,
    onReportes: () -> Unit,
    onPescadex: () -> Unit,
    onIdentificar: () -> Unit,
    onChat: () -> Unit,
    onContador: () -> Unit,
    onTorneos: () -> Unit,
    onLogros: () -> Unit,
    onSignOut: () -> Unit,
    onCloseDrawer: () -> Unit = {},
    tutorialStep: Int = -1,
    onTutorialNext: () -> Unit = {},
    onTutorialSkip: () -> Unit = {},
    onReplayTutorial: () -> Unit = {},
) {
    val entries = listOf(
        DrawerEntry("pescadex", "Pescadex", "Tu colección de especies", DrawerCyan, true),
        DrawerEntry("nuevo_parte", "Crear parte", "Registrá tu jornada", DrawerBlue, true),
        DrawerEntry("contador", "Contador", "Sumá capturas en vivo", Color(0xFF4DD7FF), true),
        DrawerEntry("chat", "Chat Huka", "Consultá con la IA", DrawerCyan, true),
        DrawerEntry("reportes", "Reportes", "Estadísticas y registros", DrawerBlue, true),
        DrawerEntry("logros", "Logros", "Tus medallas y avances", Color(0xFF5BE7D7), true),
        DrawerEntry("torneos", "Torneos", "Participá y competí", Color(0xFF59C7FF), true),
        DrawerEntry("identificar", "Identificar pez", "Analizá una fotografía", DrawerCyan, true),
        DrawerEntry("perfil", "Perfil", "Tu cuenta y ajustes", Color(0xFF8CBED8), false),
    )

    val listState = rememberLazyListState()
    var crearParteBounds by remember { mutableStateOf<Rect?>(null) }
    var identificarBounds by remember { mutableStateOf<Rect?>(null) }
    var pescadexBounds by remember { mutableStateOf<Rect?>(null) }
    var logrosBounds by remember { mutableStateOf<Rect?>(null) }

    LaunchedEffect(tutorialStep) {
        when (tutorialStep) {
            2 -> {
                crearParteBounds = null
                listState.animateScrollToItem(1)
            }
            3 -> {
                identificarBounds = null
                listState.animateScrollToItem(7)
            }
            4 -> {
                pescadexBounds = null
                listState.animateScrollToItem(0)
            }
            5 -> {
                logrosBounds = null
                listState.animateScrollToItem(5)
            }
        }
    }

    ModalDrawerSheet(
        modifier = Modifier.fillMaxHeight(),
        drawerContainerColor = DrawerBackground,
    ) {
        Box(modifier = Modifier.fillMaxSize().background(DrawerBackground)) {
            Column(modifier = Modifier.fillMaxSize()) {
                Column(modifier = Modifier.padding(horizontal = 18.dp, vertical = 18.dp)) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Surface(
                                modifier = Modifier
                                    .size(38.dp)
                                    .clickable(onClick = onCloseDrawer),
                                shape = CircleShape,
                                color = DrawerCard,
                                border = BorderStroke(1.dp, DrawerEdge.copy(alpha = 0.65f)),
                            ) {
                                Box(contentAlignment = Alignment.Center) {
                                    Text(
                                        "X",
                                        color = DrawerSubText,
                                        fontWeight = FontWeight.Bold,
                                        fontSize = 14.sp,
                                    )
                                }
                            }
                            Spacer(Modifier.width(12.dp))
                            HukaWordmark(
                                lightContent = true,
                                showSubtitle = false,
                            )
                        }

                        val initial = (user.displayName?.firstOrNull()
                            ?: user.email?.firstOrNull()
                            ?: 'H').uppercase()

                        Surface(
                            shape = CircleShape,
                            color = DrawerBlue,
                            border = BorderStroke(2.dp, Color.White.copy(alpha = 0.85f)),
                            modifier = Modifier.size(40.dp),
                        ) {
                            Box(contentAlignment = Alignment.Center) {
                                Text(
                                    initial,
                                    color = Color.White,
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.Bold,
                                )
                            }
                        }
                    }

                    Spacer(Modifier.height(20.dp))

                    val firstName = user.displayName
                        ?.trim()
                        ?.substringBefore(" ")
                        ?.takeIf { it.isNotBlank() }
                        ?: "Pescador"

                    Text(
                        "Hola $firstName",
                        fontSize = 22.sp,
                        fontWeight = FontWeight.ExtraBold,
                        color = DrawerText,
                    )
                    Text(
                        "Todo Huka, desde un solo lugar",
                        fontSize = 13.sp,
                        color = DrawerSubText,
                        modifier = Modifier.padding(top = 3.dp),
                    )
                }

                LazyColumn(
                    state = listState,
                    modifier = Modifier
                        .weight(1f)
                        .padding(horizontal = 12.dp),
                    verticalArrangement = Arrangement.spacedBy(9.dp),
                    contentPadding = PaddingValues(bottom = 14.dp),
                ) {
                    items(entries) { entry ->
                        val tutorialModifier = when (entry.key) {
                            "nuevo_parte" -> Modifier.onGloballyPositioned {
                                crearParteBounds = it.boundsInWindow()
                            }
                            "identificar" -> Modifier.onGloballyPositioned {
                                identificarBounds = it.boundsInWindow()
                            }
                            "pescadex" -> Modifier.onGloballyPositioned {
                                pescadexBounds = it.boundsInWindow()
                            }
                            "logros" -> Modifier.onGloballyPositioned {
                                logrosBounds = it.boundsInWindow()
                            }
                            else -> Modifier
                        }

                        DrawerEntryCard(
                            entry = entry,
                            selected = selected == entry.key,
                            modifier = tutorialModifier,
                            onClick = {
                                if (entry.available) {
                                    when (entry.key) {
                                        "pescadex" -> onPescadex()
                                        "nuevo_parte" -> onNuevoParte()
                                        "reportes" -> onReportes()
                                        "identificar" -> onIdentificar()
                                        "chat" -> onChat()
                                        "contador" -> onContador()
                                        "torneos" -> onTorneos()
                                        "logros" -> onLogros()
                                        else -> onInicio()
                                    }
                                }
                            },
                        )
                    }
                }

                HorizontalDivider(color = DrawerEdge.copy(alpha = 0.35f))

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable(onClick = onReplayTutorial)
                        .padding(horizontal = 18.dp, vertical = 12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Surface(
                        modifier = Modifier.size(32.dp),
                        shape = CircleShape,
                        color = DrawerCard,
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            Text(
                                "?",
                                color = DrawerBlue,
                                fontWeight = FontWeight.Bold,
                                fontSize = 16.sp,
                            )
                        }
                    }
                    Spacer(Modifier.width(12.dp))
                    Text(
                        "Ver tutorial",
                        fontSize = 14.sp,
                        color = DrawerBlue,
                        fontWeight = FontWeight.SemiBold,
                    )
                }

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable(onClick = onSignOut)
                        .padding(horizontal = 18.dp, vertical = 14.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    DrawerSignOutGlyph()
                    Spacer(Modifier.width(12.dp))
                    Text(
                        "Cerrar sesión",
                        fontSize = 14.sp,
                        color = DrawerSubText,
                    )
                }
            }

            val tutorialTarget = when (tutorialStep) {
                2 -> crearParteBounds
                3 -> identificarBounds
                4 -> pescadexBounds
                5 -> logrosBounds
                else -> null
            }

            tutorialTarget?.let { target ->
                val (title, description) = when (tutorialStep) {
                    2 -> "Registrá tu jornada" to
                        "Creá un parte cada vez que salgas a pescar. También sirve si no tuviste capturas."
                    3 -> "Identificá una especie" to
                        "Sacale una foto a un pez y Huka puede ayudarte a identificarlo."
                    4 -> "Completá tu Pescadex" to
                        "Las especies que registres se van sumando a tu colección."
                    else -> "Desbloqueá logros" to
                        "Tus registros y participación en Huka te permiten conseguir nuevos logros."
                }

                CoachMarkOverlay(
                    target = target,
                    title = title,
                    description = description,
                    stepLabel = "${tutorialStep + 1} de 6",
                    nextLabel = if (tutorialStep == 5) "Listo" else "Siguiente",
                    onNext = onTutorialNext,
                    onSkip = onTutorialSkip,
                )
            }
        }
    }
}

@Composable
private fun DrawerEntryCard(
    entry: DrawerEntry,
    selected: Boolean,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    val container = if (selected) Color(0xFF0A3B59) else DrawerCard
    val border = if (selected) entry.accent else DrawerEdge.copy(alpha = 0.48f)

    Surface(
        shape = RoundedCornerShape(16.dp),
        color = container,
        border = BorderStroke(if (selected) 1.5.dp else 1.dp, border),
        modifier = modifier
            .fillMaxWidth()
            .clickable(enabled = entry.available, onClick = onClick),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 13.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(44.dp)
                    .background(entry.accent.copy(alpha = 0.18f), RoundedCornerShape(12.dp)),
                contentAlignment = Alignment.Center,
            ) {
                DrawerGlyph(entry.key, entry.accent)
            }

            Spacer(Modifier.width(14.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    entry.title,
                    color = DrawerText,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.Bold,
                )
                Text(
                    entry.subtitle,
                    fontSize = 11.sp,
                    color = DrawerSubText,
                    modifier = Modifier.padding(top = 2.dp),
                )
            }

            if (!entry.available) {
                Text(
                    "PRÓX.",
                    fontSize = 9.sp,
                    color = entry.accent,
                    fontWeight = FontWeight.Bold,
                )
            } else {
                DrawerChevron(entry.accent)
            }
        }
    }
}

@Composable
private fun DrawerGlyph(key: String, color: Color) {
    Canvas(modifier = Modifier.size(24.dp)) {
        val stroke = 2.dp.toPx()

        when (key) {
            "pescadex" -> {
                drawOval(
                    color = color,
                    topLeft = Offset(size.width * 0.12f, size.height * 0.28f),
                    size = androidx.compose.ui.geometry.Size(size.width * 0.58f, size.height * 0.44f),
                    style = Stroke(width = stroke),
                )
                val tail = Path().apply {
                    moveTo(size.width * 0.68f, size.height * 0.50f)
                    lineTo(size.width * 0.90f, size.height * 0.30f)
                    lineTo(size.width * 0.90f, size.height * 0.70f)
                    close()
                }
                drawPath(tail, color, style = Stroke(width = stroke))
                drawCircle(color, radius = size.minDimension * 0.035f, center = Offset(size.width * 0.30f, size.height * 0.43f))
            }

            "nuevo_parte" -> {
                drawLine(color, Offset(size.width * 0.50f, size.height * 0.18f), Offset(size.width * 0.50f, size.height * 0.82f), stroke, StrokeCap.Round)
                drawLine(color, Offset(size.width * 0.18f, size.height * 0.50f), Offset(size.width * 0.82f, size.height * 0.50f), stroke, StrokeCap.Round)
            }

            "contador" -> {
                listOf(0.25f, 0.50f, 0.75f).forEach { x ->
                    drawLine(color, Offset(size.width * x, size.height * 0.20f), Offset(size.width * x, size.height * 0.80f), stroke, StrokeCap.Round)
                }
            }

            "chat" -> {
                val bubble = Path().apply {
                    moveTo(size.width * 0.16f, size.height * 0.20f)
                    lineTo(size.width * 0.84f, size.height * 0.20f)
                    lineTo(size.width * 0.84f, size.height * 0.66f)
                    lineTo(size.width * 0.48f, size.height * 0.66f)
                    lineTo(size.width * 0.30f, size.height * 0.84f)
                    lineTo(size.width * 0.30f, size.height * 0.66f)
                    lineTo(size.width * 0.16f, size.height * 0.66f)
                    close()
                }
                drawPath(bubble, color, style = Stroke(width = stroke))
            }

            "reportes" -> {
                drawRect(
                    color = color,
                    topLeft = Offset(size.width * 0.18f, size.height * 0.16f),
                    size = androidx.compose.ui.geometry.Size(size.width * 0.64f, size.height * 0.68f),
                    style = Stroke(width = stroke),
                )
                listOf(0.36f, 0.50f, 0.64f).forEach { y ->
                    drawLine(color, Offset(size.width * 0.30f, size.height * y), Offset(size.width * 0.70f, size.height * y), stroke, StrokeCap.Round)
                }
            }

            "logros" -> {
                drawCircle(color = color, radius = size.minDimension * 0.24f, center = Offset(size.width * 0.50f, size.height * 0.42f), style = Stroke(width = stroke))
                drawLine(color, Offset(size.width * 0.38f, size.height * 0.64f), Offset(size.width * 0.31f, size.height * 0.84f), stroke)
                drawLine(color, Offset(size.width * 0.62f, size.height * 0.64f), Offset(size.width * 0.69f, size.height * 0.84f), stroke)
            }

            "torneos" -> {
                val cup = Path().apply {
                    moveTo(size.width * 0.28f, size.height * 0.20f)
                    lineTo(size.width * 0.72f, size.height * 0.20f)
                    lineTo(size.width * 0.66f, size.height * 0.50f)
                    lineTo(size.width * 0.54f, size.height * 0.62f)
                    lineTo(size.width * 0.46f, size.height * 0.62f)
                    lineTo(size.width * 0.34f, size.height * 0.50f)
                    close()
                }
                drawPath(cup, color, style = Stroke(width = stroke))
                drawLine(color, Offset(size.width * 0.50f, size.height * 0.62f), Offset(size.width * 0.50f, size.height * 0.79f), stroke)
                drawLine(color, Offset(size.width * 0.34f, size.height * 0.82f), Offset(size.width * 0.66f, size.height * 0.82f), stroke, StrokeCap.Round)
            }

            "identificar" -> {
                drawCircle(color = color, radius = size.minDimension * 0.16f, center = center, style = Stroke(width = stroke))
                val inset = size.width * 0.18f
                val short = size.width * 0.18f
                drawLine(color, Offset(inset, inset), Offset(inset + short, inset), stroke)
                drawLine(color, Offset(inset, inset), Offset(inset, inset + short), stroke)
                drawLine(color, Offset(size.width - inset, inset), Offset(size.width - inset - short, inset), stroke)
                drawLine(color, Offset(size.width - inset, inset), Offset(size.width - inset, inset + short), stroke)
                drawLine(color, Offset(inset, size.height - inset), Offset(inset + short, size.height - inset), stroke)
                drawLine(color, Offset(inset, size.height - inset), Offset(inset, size.height - inset - short), stroke)
                drawLine(color, Offset(size.width - inset, size.height - inset), Offset(size.width - inset - short, size.height - inset), stroke)
                drawLine(color, Offset(size.width - inset, size.height - inset), Offset(size.width - inset, size.height - inset - short), stroke)
            }

            else -> {
                drawCircle(color = color, radius = size.minDimension * 0.15f, center = Offset(size.width * 0.50f, size.height * 0.34f), style = Stroke(width = stroke))
                drawArc(
                    color = color,
                    startAngle = 200f,
                    sweepAngle = 140f,
                    useCenter = false,
                    topLeft = Offset(size.width * 0.22f, size.height * 0.46f),
                    size = androidx.compose.ui.geometry.Size(size.width * 0.56f, size.height * 0.42f),
                    style = Stroke(width = stroke),
                )
            }
        }
    }
}

@Composable
private fun DrawerChevron(color: Color) {
    Canvas(modifier = Modifier.size(20.dp)) {
        val stroke = 2.dp.toPx()
        drawLine(color, Offset(size.width * 0.34f, size.height * 0.22f), Offset(size.width * 0.64f, size.height * 0.50f), stroke, StrokeCap.Round)
        drawLine(color, Offset(size.width * 0.64f, size.height * 0.50f), Offset(size.width * 0.34f, size.height * 0.78f), stroke, StrokeCap.Round)
    }
}

@Composable
private fun DrawerSignOutGlyph() {
    Canvas(modifier = Modifier.size(30.dp)) {
        val color = DrawerSubText
        val stroke = 2.dp.toPx()
        drawLine(color, Offset(size.width * 0.18f, size.height * 0.20f), Offset(size.width * 0.18f, size.height * 0.80f), stroke)
        drawLine(color, Offset(size.width * 0.18f, size.height * 0.20f), Offset(size.width * 0.48f, size.height * 0.20f), stroke)
        drawLine(color, Offset(size.width * 0.45f, size.height * 0.50f), Offset(size.width * 0.82f, size.height * 0.50f), stroke, StrokeCap.Round)
        drawLine(color, Offset(size.width * 0.66f, size.height * 0.34f), Offset(size.width * 0.82f, size.height * 0.50f), stroke, StrokeCap.Round)
        drawLine(color, Offset(size.width * 0.66f, size.height * 0.66f), Offset(size.width * 0.82f, size.height * 0.50f), stroke, StrokeCap.Round)
    }
}
